import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSubscriptionsEnhanced = () => {
  const [activeTab, setActiveTab] = useState('subscriptions'); // 'subscriptions' or 'audit'
  const [loading, setLoading] = useState(true);
  
  // Subscriptions tab state
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionSummary, setSubscriptionSummary] = useState(null);
  const [filterTier, setFilterTier] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  
  // Audit tab state
  const [auditData, setAuditData] = useState([]);
  const [auditSummary, setAuditSummary] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [providerDetails, setProviderDetails] = useState(null);
  const [showProviderDetails, setShowProviderDetails] = useState(false);

  useEffect(() => {
    // Set default date range to current month
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    setDateFrom(firstDay.toISOString().split('T')[0]);
    setDateTo(lastDay.toISOString().split('T')[0]);
    
    if (activeTab === 'subscriptions') {
      fetchSubscriptions();
    } else {
      fetchAuditData(firstDay.toISOString().split('T')[0], lastDay.toISOString().split('T')[0]);
    }
  }, [activeTab]);

  const getAdminToken = () => {
    return localStorage.getItem('sessionToken');
  };

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const token = getAdminToken();
      const response = await axios.get(`${BACKEND_URL}/api/admin/subscriptions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSubscriptions(response.data.subscriptions);
      setSubscriptionSummary(response.data.summary);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      alert('Failed to fetch subscriptions. Please ensure you have admin access.');
      setLoading(false);
    }
  };

  const fetchAuditData = async (from, to) => {
    try {
      setLoading(true);
      const token = getAdminToken();
      const response = await axios.get(`${BACKEND_URL}/api/admin/provider-audit`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { date_from: from || dateFrom, date_to: to || dateTo }
      });
      
      setAuditData(response.data.providers);
      setAuditSummary(response.data.summary);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching audit data:', error);
      alert('Failed to fetch audit data. Please ensure you have admin access.');
      setLoading(false);
    }
  };

  const fetchProviderDetails = async (providerId) => {
    try {
      const token = getAdminToken();
      const response = await axios.get(`${BACKEND_URL}/api/admin/provider-audit/${providerId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { date_from: dateFrom, date_to: dateTo }
      });
      
      setProviderDetails(response.data);
      setShowProviderDetails(true);
    } catch (error) {
      console.error('Error fetching provider details:', error);
      alert('Failed to fetch provider details.');
    }
  };

  const handleExportCSV = async () => {
    try {
      const token = getAdminToken();
      const response = await axios.get(`${BACKEND_URL}/api/admin/export-payroll`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { date_from: dateFrom, date_to: dateTo },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payroll_${dateFrom}_to_${dateTo}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      alert('Payroll CSV exported successfully!');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export payroll CSV.');
    }
  };

  const handleMarkAsPaid = async (providerId, providerName) => {
    if (!window.confirm(`Mark payroll as paid for ${providerName}?\nDate Range: ${dateFrom} to ${dateTo}`)) {
      return;
    }
    
    try {
      const token = getAdminToken();
      await axios.post(`${BACKEND_URL}/api/admin/payroll/mark-paid`, {
        provider_id: providerId,
        date_from: dateFrom,
        date_to: dateTo
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Payroll marked as paid successfully!');
      fetchAuditData(dateFrom, dateTo);
    } catch (error) {
      console.error('Error marking as paid:', error);
      alert('Failed to mark payroll as paid.');
    }
  };

  const handleDateFilterChange = () => {
    if (dateFrom && dateTo) {
      fetchAuditData(dateFrom, dateTo);
    }
  };

  const setQuickDateRange = (range) => {
    const now = new Date();
    let from, to;
    
    switch(range) {
      case 'current_month':
        from = new Date(now.getFullYear(), now.getMonth(), 1);
        to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'last_month':
        from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        to = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'current_year':
        from = new Date(now.getFullYear(), 0, 1);
        to = new Date(now.getFullYear(), 11, 31);
        break;
      case 'last_3_months':
        from = new Date(now.getFullYear(), now.getMonth() - 3, 1);
        to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      default:
        return;
    }
    
    setDateFrom(from.toISOString().split('T')[0]);
    setDateTo(to.toISOString().split('T')[0]);
    fetchAuditData(from.toISOString().split('T')[0], to.toISOString().split('T')[0]);
  };

  const filteredSubscriptions = subscriptions.filter(sub => {
    const matchesTier = filterTier === 'all' || sub.subscription_tier === filterTier;
    const matchesSearch = sub.provider_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         sub.provider_email.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTier && matchesSearch;
  });

  const getTierColor = (tier) => {
    switch(tier) {
      case 'Enterprise': return 'bg-gradient-to-r from-amber-500 to-orange-500 text-white';
      case 'Pro': return 'bg-gradient-to-r from-purple-500 to-pink-500 text-white';
      case 'Basic': return 'bg-gradient-to-r from-sky-500 to-cyan-500 text-white';
      case 'Free': return 'bg-gray-500 text-white';
      default: return 'bg-gray-400 text-white';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 font-semibold">Active</span>;
    }
    return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800 font-semibold">Free</span>;
  };

  const getPayrollStatusBadge = (status) => {
    switch(status) {
      case 'paid':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 font-semibold">Paid</span>;
      case 'partial':
        return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800 font-semibold">Partial</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800 font-semibold">Pending</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Provider Management</h1>
          <p className="text-gray-600">Manage subscriptions, audit finances, and track payroll</p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('subscriptions')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'subscriptions'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              💳 Subscriptions
            </button>
            <button
              onClick={() => setActiveTab('audit')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'audit'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📊 Provider Audit & Payroll
            </button>
          </nav>
        </div>

        {/* SUBSCRIPTIONS TAB */}
        {activeTab === 'subscriptions' && (
          <>
            {/* Summary Cards */}
            {subscriptionSummary && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Total Providers</p>
                      <p className="text-3xl font-bold text-gray-900">{subscriptionSummary.total_providers}</p>
                    </div>
                    <div className="bg-blue-100 rounded-full p-3">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Paid Subscribers</p>
                      <p className="text-3xl font-bold text-green-600">{subscriptionSummary.total_subscribers}</p>
                    </div>
                    <div className="bg-green-100 rounded-full p-3">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Active Subscriptions</p>
                      <p className="text-3xl font-bold text-blue-600">{subscriptionSummary.active_subscriptions}</p>
                    </div>
                    <div className="bg-blue-100 rounded-full p-3">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
                      <p className="text-3xl font-bold text-amber-600">{subscriptionSummary.total_revenue.toFixed(2)} KWD</p>
                    </div>
                    <div className="bg-amber-100 rounded-full p-3">
                      <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tier Distribution */}
            {subscriptionSummary && (
              <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Tier Distribution</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-gray-700">{subscriptionSummary.free_tier_count}</p>
                    <p className="text-sm text-gray-600">Free</p>
                  </div>
                  <div className="text-center p-4 bg-sky-50 rounded-lg">
                    <p className="text-2xl font-bold text-sky-600">{subscriptionSummary.basic_tier_count}</p>
                    <p className="text-sm text-gray-600">Basic</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">{subscriptionSummary.pro_tier_count}</p>
                    <p className="text-sm text-gray-600">Pro</p>
                  </div>
                  <div className="text-center p-4 bg-amber-50 rounded-lg">
                    <p className="text-2xl font-bold text-amber-600">{subscriptionSummary.enterprise_tier_count}</p>
                    <p className="text-sm text-gray-600">Enterprise</p>
                  </div>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                  <input
                    type="text"
                    placeholder="Search by provider name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="w-full md:w-48">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Tier</label>
                  <select
                    value={filterTier}
                    onChange={(e) => setFilterTier(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Tiers</option>
                    <option value="Free">Free</option>
                    <option value="Basic">Basic</option>
                    <option value="Pro">Pro</option>
                    <option value="Enterprise">Enterprise</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={fetchSubscriptions}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Refresh
                  </button>
                </div>
              </div>
            </div>

            {/* Subscriptions Table */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Provider
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tier
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Start Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        End Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Revenue
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Payments
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredSubscriptions.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                          No subscriptions found
                        </td>
                      </tr>
                    ) : (
                      filteredSubscriptions.map((sub) => (
                        <tr key={sub.provider_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{sub.provider_name}</div>
                            <div className="text-sm text-gray-500">{sub.provider_email}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getTierColor(sub.subscription_tier)}`}>
                              {sub.subscription_tier}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(sub.status)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(sub.subscription_start_date)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(sub.subscription_end_date)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {sub.total_revenue.toFixed(2)} KWD
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {sub.payment_count}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => {
                                setSelectedSubscription(sub);
                                setShowPaymentHistory(true);
                              }}
                              className="text-blue-600 hover:text-blue-900 mr-3"
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* AUDIT TAB */}
        {activeTab === 'audit' && (
          <>
            {/* Date Filters and Export */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <div className="flex flex-col gap-4">
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setQuickDateRange('current_month')}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                  >
                    Current Month
                  </button>
                  <button
                    onClick={() => setQuickDateRange('last_month')}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                  >
                    Last Month
                  </button>
                  <button
                    onClick={() => setQuickDateRange('last_3_months')}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                  >
                    Last 3 Months
                  </button>
                  <button
                    onClick={() => setQuickDateRange('current_year')}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                  >
                    Current Year
                  </button>
                </div>
                
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <button
                      onClick={handleDateFilterChange}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                    >
                      Apply Filter
                    </button>
                    <button
                      onClick={handleExportCSV}
                      className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap"
                    >
                      📥 Export CSV
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Audit Summary Cards */}
            {auditSummary && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-lg shadow-md p-6">
                  <p className="text-sm text-gray-600 mb-1">Total Appointments</p>
                  <p className="text-3xl font-bold text-gray-900">{auditSummary.total_appointments}</p>
                  <div className="mt-2 text-xs text-gray-500">
                    <span className="text-green-600">✓ {auditSummary.total_completed}</span> completed •{' '}
                    <span className="text-red-600">✗ {auditSummary.total_cancelled}</span> cancelled •{' '}
                    <span className="text-orange-600">⊘ {auditSummary.total_no_show}</span> no-show
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6">
                  <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
                  <p className="text-3xl font-bold text-blue-600">{auditSummary.total_revenue.toFixed(3)} KWD</p>
                  <p className="text-xs text-gray-500 mt-2">
                    {auditSummary.date_range.from} to {auditSummary.date_range.to}
                  </p>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6">
                  <p className="text-sm text-gray-600 mb-1">Commission Owed</p>
                  <p className="text-3xl font-bold text-amber-600">{auditSummary.total_commission_owed.toFixed(3)} KWD</p>
                  <p className="text-xs text-gray-500 mt-2">Platform earnings</p>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6">
                  <p className="text-sm text-gray-600 mb-1">Total Refunds</p>
                  <p className="text-3xl font-bold text-red-600">{auditSummary.total_refunds.toFixed(3)} KWD</p>
                  <p className="text-xs text-gray-500 mt-2">Credited to wallets</p>
                </div>
              </div>
            )}

            {/* Audit Table */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Provider
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tier
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Appointments
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Revenue
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Provider Earnings
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Commission
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Payroll Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {auditData.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                          No audit data available for selected date range
                        </td>
                      </tr>
                    ) : (
                      auditData.map((provider) => (
                        <tr key={provider.provider_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{provider.provider_name}</div>
                            <div className="text-sm text-gray-500">{provider.provider_email}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getTierColor(provider.subscription_tier)}`}>
                              {provider.subscription_tier}
                            </span>
                            <div className="text-xs text-gray-500 mt-1">{provider.commission_rate}% rate</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{provider.appointments.total}</div>
                            <div className="text-xs text-gray-500">
                              ✓{provider.appointments.completed} ✗{provider.appointments.cancelled} ⊘{provider.appointments.no_show}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{provider.financial.total_revenue.toFixed(3)} KWD</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-green-600">{provider.financial.provider_earnings.toFixed(3)} KWD</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-amber-600">{provider.financial.commission_owed.toFixed(3)} KWD</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getPayrollStatusBadge(provider.payroll.status)}
                            <div className="text-xs text-gray-500 mt-1">
                              {provider.payroll.pending_appointments} pending
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => fetchProviderDetails(provider.provider_id)}
                              className="text-blue-600 hover:text-blue-900 mr-2"
                            >
                              Details
                            </button>
                            {provider.payroll.pending_appointments > 0 && (
                              <button
                                onClick={() => handleMarkAsPaid(provider.provider_id, provider.provider_name)}
                                className="text-green-600 hover:text-green-900"
                              >
                                Mark Paid
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Payment History Modal (Subscriptions Tab) */}
      {showPaymentHistory && selectedSubscription && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">{selectedSubscription.provider_name}</h3>
                  <p className="text-gray-600 mt-1">{selectedSubscription.provider_email}</p>
                </div>
                <button
                  onClick={() => {
                    setShowPaymentHistory(false);
                    setSelectedSubscription(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                <div>
                  <p className="text-sm text-gray-600">Tier</p>
                  <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full mt-1 ${getTierColor(selectedSubscription.subscription_tier)}`}>
                    {selectedSubscription.subscription_tier}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Start Date</p>
                  <p className="text-sm font-medium text-gray-900 mt-1">{formatDate(selectedSubscription.subscription_start_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">End Date</p>
                  <p className="text-sm font-medium text-gray-900 mt-1">{formatDate(selectedSubscription.subscription_end_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Revenue</p>
                  <p className="text-sm font-bold text-green-600 mt-1">{selectedSubscription.total_revenue.toFixed(2)} KWD</p>
                </div>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Payment History ({selectedSubscription.payment_count} payments)</h4>
              
              {selectedSubscription.payment_history.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No payment history available</p>
              ) : (
                <div className="space-y-3">
                  {selectedSubscription.payment_history.map((payment) => (
                    <div key={payment.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <p className="font-medium text-gray-900">{payment.amount} {payment.currency}</p>
                            <span className={`px-2 py-1 text-xs rounded-full font-semibold ${
                              payment.status === 'completed' ? 'bg-green-100 text-green-800' :
                              payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {payment.status}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 space-y-1">
                            <p>Date: {formatDate(payment.created_at)}</p>
                            <p>Method: {payment.payment_method}</p>
                            {payment.transaction_id && <p>Transaction: {payment.transaction_id}</p>}
                            {payment.invoice_id && <p>Invoice: {payment.invoice_id}</p>}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Provider Details Modal (Audit Tab) */}
      {showProviderDetails && providerDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden my-8">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">{providerDetails.provider.name}</h3>
                  <p className="text-gray-600 mt-1">{providerDetails.provider.email}</p>
                  <div className="mt-2">
                    <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${getTierColor(providerDetails.provider.subscription_tier)}`}>
                      {providerDetails.provider.subscription_tier} - {providerDetails.provider.commission_rate}% Commission
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowProviderDetails(false);
                    setProviderDetails(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                <div>
                  <p className="text-sm text-gray-600">Total Appointments</p>
                  <p className="text-2xl font-bold text-gray-900">{providerDetails.summary.total_appointments}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Completed</p>
                  <p className="text-2xl font-bold text-green-600">{providerDetails.summary.completed}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Revenue</p>
                  <p className="text-2xl font-bold text-blue-600">{providerDetails.summary.total_revenue.toFixed(3)} KWD</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Provider Earnings</p>
                  <p className="text-2xl font-bold text-green-600">{providerDetails.summary.provider_earnings.toFixed(3)} KWD</p>
                </div>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Appointment Details</h4>
              
              {providerDetails.appointments.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No appointments for this date range</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Date/Time</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">User</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Service</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Fee</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Provider</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Platform</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Payroll</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {providerDetails.appointments.map((apt) => (
                        <tr key={apt.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {apt.date}<br/>{apt.time}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">{apt.patient_name}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{apt.service_name}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 text-xs rounded-full font-semibold ${
                              apt.status === 'completed' ? 'bg-green-100 text-green-800' :
                              apt.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                              apt.status === 'no_show' ? 'bg-orange-100 text-orange-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {apt.status}
                              {apt.is_no_show_split && <span className="ml-1">50/50</span>}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{apt.fee.toFixed(3)}</td>
                          <td className="px-4 py-3 text-sm font-medium text-green-600">{apt.provider_share.toFixed(3)}</td>
                          <td className="px-4 py-3 text-sm font-medium text-amber-600">{apt.platform_share.toFixed(3)}</td>
                          <td className="px-4 py-3 text-sm">
                            {getPayrollStatusBadge(apt.payroll_status)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSubscriptionsEnhanced;
